import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQueryClient } from "@tanstack/react-query";
import { useGoogleLogin } from "@react-oauth/google";
import ClientLayout from "../../../layouts/ClientLayout";
import PhoneInput from "react-phone-number-input";
import { authService } from "../../../lib/api/apiService";
import { Eye, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getRecaptchaToken, preloadRecaptcha } from "../../../utils/recaptcha";
import RecaptchaLegalNotice from "../../../components/RecaptchaLegalNotice";

const createRegisterSchema = (t) =>
  z
    .object({
      lastName: z
        .string()
        .trim()
        .min(1, t("register.errors.last_name_required")),
      firstName: z
        .string()
        .trim()
        .min(1, t("register.errors.first_name_required")),
      email: z
        .string()
        .trim()
        .min(1, t("register.errors.email_required"))
        .email(t("register.errors.email_invalid")),
      phone: z.string(),
      password: z
        .string()
        .min(6, t("register.errors.password_min_length")),
      confirmPassword: z
        .string()
        .min(1, t("register.errors.confirm_password_required")),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("register.errors.password_mismatch"),
      path: ["confirmPassword"],
    });

export default function UserRegisterPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const schema = useMemo(
    () => createRegisterSchema(t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language]
  );

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  useEffect(() => {
    preloadRecaptcha();
  }, []);

  // Finalise une session après une auth Google réussie : persiste le token
  // (localStorage car l'utilisateur vient explicitement de créer son compte),
  // pousse l'utilisateur dans la query `current-user`, et redirige selon le rôle.
  const finalizeLogin = useCallback(
    (accessToken, user) => {
      if (!accessToken || !user) {
        setError(t("auth.errors.missing_info"));
        return;
      }

      localStorage.setItem("access_token", accessToken);
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("remember_me", "true");
      sessionStorage.removeItem("access_token");
      sessionStorage.removeItem("user");

      queryClient.setQueryData(["current-user"], user);
      queryClient.invalidateQueries({ queryKey: ["current-user"] });

      switch (user.role) {
        case "CLIENT":
          navigate("/");
          break;
        case "SELLER":
          navigate("/dashboard");
          break;
        case "ADMIN":
          navigate("/admin-dashboard");
          break;
        default:
          navigate("/");
      }
    },
    [navigate, t, queryClient]
  );

  const handleGoogleLoginSuccess = async (response) => {
    setGoogleLoading(true);
    setError("");

    try {
      const result = await authService.loginWithGoogle(response.access_token);

      if (!result) {
        setError(t("auth.errors.invalid_response"));
        return;
      }

      if (result.success === false) {
        setError(result.error || t("auth.errors.google_error"));
        return;
      }

      const statusCode = result.statusCode ?? result.status ?? result.code;
      const payload = result.data ?? result.payload ?? {};
      const accessToken =
        payload.access_token ?? payload.accessToken ?? result.access_token;
      const user = payload.user ?? result.user;

      if (
        (statusCode && String(statusCode) === "200") ||
        result.success === true ||
        (accessToken && user)
      ) {
        finalizeLogin(accessToken, user);
      } else {
        setError(result.message || t("auth.errors.google_error"));
      }
    } catch (err) {
      setError(t("auth.errors.google_error"));
      console.error(err);
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleLoginError = () => {
    setError(t("auth.errors.google_auth_error"));
    setGoogleLoading(false);
  };

  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleLoginSuccess,
    onError: handleGoogleLoginError,
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      lastName: "",
      firstName: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
    },
  });

  const inputBase =
    "w-full border rounded-lg px-3 p-2 focus:ring-2 focus:ring-[#E5502E] outline-none";
  const inputClass = (hasError) =>
    `${inputBase} ${hasError ? "border-red-500 focus:ring-red-200" : ""}`;

  const onSubmit = handleSubmit(async (formValues) => {
    setError("");
    setSuccessMessage("");
    setSubmitted(false);

    const payload = {
      firstName: formValues.firstName.trim(),
      lastName: formValues.lastName.trim(),
      email: formValues.email.trim().toLowerCase(),
      phone: (formValues.phone || "").trim(),
      password: formValues.password,
      confirmPassword: formValues.confirmPassword,
      role: "CLIENT",
    };

    try {
      const recaptchaToken = await getRecaptchaToken('register');
      const result = await authService.registerClient({
        ...payload,
        recaptchaToken,
      });

      if (result.success) {
        setSubmitted(true);
        setSuccessMessage(
          result.data?.message || t("register.success.account_created")
        );
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        setError(result.error || t("register.errors.register_error"));
      }
    } catch (err) {
      setError(
        err?.message?.includes('reCAPTCHA') || err?.message?.includes('Token')
          ? err.message
          : t("register.errors.unexpected_error")
      );
    }
  });

  return (
    <ClientLayout>
      <div className="">
        <div className="w-full">
          <div
            className="pt-12 pb-20 flex items-center justify-center bg-cover bg-center relative"
            style={{
              backgroundImage: "url('/images/family-6398107_1280.jpg')",
              backgroundPosition: "center",
              backgroundSize: "cover",
            }}
          >
            <div className="absolute inset-0 bg-[#F6F6F6] opacity-95"></div>

            <div className="relative max-w-xl w-full px-8">
              <h1 className="text-3xl font-poppins font-bold mb-6 flex justify-center whitespace-nowrap">
                {t("register.title")}{" "}
                <span className="text-[#E5502E] ml-2">kilimmanjaro</span>
              </h1>

              {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}
              {submitted && successMessage && (
                <div className="mb-4 p-3 bg-green-100 border border-green-300 text-green-700 rounded-lg text-sm">
                  {successMessage}
                </div>
              )}

              <form onSubmit={onSubmit} className="space-y-4" noValidate>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-1 text-start text-[#141C1D] text-[16px] font-semibold">
                      {t("register.last_name_label")}
                    </label>
                    <input
                      type="text"
                      autoComplete="family-name"
                      {...register("lastName")}
                      className={inputClass(!!errors.lastName)}
                      placeholder={t("register.last_name_placeholder")}
                    />
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.lastName.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block mb-1 text-start text-[#141C1D] text-[16px] font-semibold">
                      {t("register.first_name_label")}
                    </label>
                    <input
                      type="text"
                      autoComplete="given-name"
                      {...register("firstName")}
                      className={inputClass(!!errors.firstName)}
                      placeholder={t("register.first_name_placeholder")}
                    />
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.firstName.message}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block mb-1 text-start text-[#141C1D] text-[16px] font-semibold">
                    {t("register.email_label")}
                  </label>
                  <input
                    type="email"
                    autoComplete="email"
                    {...register("email")}
                    className={inputClass(!!errors.email)}
                    placeholder={t("register.email_placeholder")}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block mb-1 text-start text-[#141C1D] text-[16px] font-semibold">
                    {t("register.phone_label")}
                  </label>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <PhoneInput
                        international
                        defaultCountry="TG"
                        value={field.value || undefined}
                        onChange={(v) => field.onChange(v ?? "")}
                        placeholder={t("register.phone_placeholder")}
                        className={`w-full px-4 py-1.5 pr-0 border rounded-lg bg-gray-50 focus:border-transparent ${
                          errors.phone ? "border-red-500" : ""
                        }`}
                      />
                    )}
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.phone.message}
                    </p>
                  )}
                </div>

                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block mb-1 text-start text-[#141C1D] text-[16px] font-semibold">
                      {t("register.password_label")}
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        {...register("password")}
                        className={`${inputClass(!!errors.password)} pr-12`}
                        placeholder={t("register.password_placeholder")}
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-500 hover:text-gray-700 border-0 bg-transparent shadow-none outline-none focus:outline-none focus:ring-0"
                        aria-label={
                          showPassword
                            ? t("register.hide_password")
                            : t("register.show_password")
                        }
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.password.message}
                      </p>
                    )}
                  </div>

                  <div className="flex-1">
                    <label className="block mb-1 text-start text-[#141C1D] text-[16px] font-semibold">
                      {t("register.confirm_password_label")}
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        autoComplete="new-password"
                        {...register("confirmPassword")}
                        className={`${inputClass(
                          !!errors.confirmPassword
                        )} pr-12`}
                        placeholder={t("register.password_placeholder")}
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-500 hover:text-gray-700 border-0 bg-transparent shadow-none outline-none focus:outline-none focus:ring-0"
                        aria-label={
                          showConfirmPassword
                            ? t("register.hide_confirm_password")
                            : t("register.show_confirm_password")
                        }
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.confirmPassword.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm text-gray-600">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" className="accent-[#E5502E]" />{" "}
                    {t("register.remember_me")}
                  </label>
                  <span
                    onClick={() => navigate("/reset-password")}
                    className="text-[#E5502E] font-medium cursor-pointer hover:underline"
                  >
                    {t("register.forgot_password")}
                  </span>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || googleLoading}
                  className="w-full bg-[#E5502E] text-white py-2 rounded-full hover:bg-[#cc4527] transition font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSubmitting
                    ? t("register.register_loading")
                    : t("register.register_button")}
                </button>

                <RecaptchaLegalNotice className="text-center" />

                <button
                  type="button"
                  onClick={() => googleLogin()}
                  disabled={googleLoading || isSubmitting}
                  className="w-full border border-gray-300 font-medium text-gray-700 py-2 rounded-full flex items-center justify-center hover:bg-gray-100 transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {googleLoading ? (
                    <span className="text-sm">
                      {t("register.google_loading", "Connexion Google en cours...")}
                    </span>
                  ) : (
                    <>
                      <img
                        src="/images/google-icon.png"
                        alt="Google"
                        className="w-5 h-5 mr-2"
                      />
                      {t("register.login_with_google")}
                    </>
                  )}
                </button>
              </form>

              <p className="mt-4 text-center  font-medium text-sm text-gray-600">
                {t("register.already_account")}{" "}
                <span
                  onClick={() => navigate("/login")}
                  className="text-[#E5502E] cursor-pointer hover:underline"
                >
                  {t("register.login_link")}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </ClientLayout>
  );
}
